/**
 * Browser Fingerprinting Utility
 *
 * Genera una huella digital única del navegador para validación de session tokens.
 * La huella incluye características del navegador que son difíciles de falsificar.
 */

/**
 * Genera un fingerprint único del navegador
 * @returns {Promise<string>} Hash SHA-256 del fingerprint
 */
export async function generateFingerprint() {
  try {
    const data = {
      // Información básica del navegador
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages ? Array.from(navigator.languages) : [],

      // Zona horaria
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),

      // Información de pantalla
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        colorDepth: window.screen.colorDepth,
        pixelDepth: window.screen.pixelDepth,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
      },

      // Plataforma
      platform: navigator.platform,

      // Hardware
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      deviceMemory: navigator.deviceMemory || 0,
      maxTouchPoints: navigator.maxTouchPoints || 0,

      // Características adicionales
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack || 'unknown',

      // Plugins (solo cantidad para no ser muy específico)
      pluginsLength: navigator.plugins ? navigator.plugins.length : 0,
    }

    // Convertir a string JSON
    const jsonString = JSON.stringify(data)

    // Generar hash SHA-256
    const hash = await hashString(jsonString)

    return hash
  } catch (error) {
    console.error('[Fingerprint] Error generating fingerprint:', error)
    // Fallback: usar solo userAgent
    return await hashString(navigator.userAgent)
  }
}

/**
 * Genera un hash SHA-256 de un string
 * @param {string} str - String a hashear
 * @returns {Promise<string>} Hash en formato hexadecimal
 */
async function hashString(str) {
  try {
    // Usar Web Crypto API
    const encoder = new TextEncoder()
    const data = encoder.encode(str)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')

    return hashHex
  } catch (error) {
    console.error('[Fingerprint] Error hashing string:', error)
    // Fallback simple (no criptográfico)
    return simpleHash(str)
  }
}

/**
 * Hash simple como fallback (no criptográfico)
 * @param {string} str - String a hashear
 * @returns {string} Hash simple
 */
function simpleHash(str) {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16)
}

/**
 * Obtiene información del navegador para debugging
 * @returns {object} Información del navegador
 */
export function getBrowserInfo() {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookieEnabled: navigator.cookieEnabled,
  }
}
